import { useEffect, useState } from "react";
import {
  LeftCircleOutlined,
  LoadingOutlined,
  PlusCircleOutlined,
  DollarCircleOutlined,
  CreditCardOutlined,
  StopOutlined,
  PauseCircleOutlined,
  FilePdfOutlined,
  MailOutlined,
  EyeOutlined,
  UploadOutlined
} from "@ant-design/icons";
import { Button, Col, Descriptions, Form, Modal, Row, Select, notification } from "antd";
import {
  useCancelSurveyMutation,
  useCreateCustomerConnectLogForSurveyMutation,
  useUpdateCustomerConnectLogForSurveyMutation,
  useGetCustomerConnectLogsForSurveyQuery,
  useLazyGetSurveyByIdQuery,
  useOnHoldSurveyMutation,
  useGetRefundsForSurveyQuery,
  useGetPaymentsForSurveyQuery
} from "../../../redux/services/surveyAPI";
import { useGetOrderItemTypesQuery } from "../../../redux/services/orderItemTypeApi";
import { useAppSelector } from "../../../redux/hooks";
import { useNavigate, useParams } from "react-router-dom";
import ConnectLogForm from "../ConnectLogForm";
import OrderItemForm from "../OrderItemForm";
import ConnectLogTabel from "../ConnectLogTabel";
import OrderItemTable from "../OrderItemTable";
import ErrorDetails from "../../../components/ErrorDetails";
import PaymentTable from "../FinanceInstallation/PaymentTable";
import RefundTable from "../FinanceInstallation/RefundTable";
import {
  localizeDateTime,
  localizeDate,
  customerAddress,
  isUserHasRoleTypes,
  getStatusText
} from "../../../utils";
import { holdOptions, cancelOptions } from "../../../constants/helperConstants";
import AddPaymentModal from "../FinanceSurvey/AddPaymentModal";
import UploadInvoicePdfModal from "./UploadInvoicePdfModal";
import {
  useUpdatePaymentMutation,
  useUpdateRefundMutation,
  useDeletePaymentMutation,
  useDeleteRefundMutation,
  useCreateOrderItemMutation,
  useUpdateOrderItemMutation,
  useDeleteOrderItemMutation,
  useGetOrderItemsQuery,
  useSendQuotationMutation,
  useSendPaymentDataMutation,
  useSendQuotationPdfMutation,
  useSendInvoiceMutation,
  useViewQuotationReportMutation,
  useViewInvoiceReportMutation
} from "../../../redux/services/assignmentApi";
import { BASE_URL } from "../../../redux/services/baseApi";

const ViewSurvey = (props: any) => {
  const auth = useAppSelector<any>((store) => store.auth);
  const navigate = useNavigate();
  const params = useParams();

  const [api, contextHolder] = notification.useNotification();
  const [form] = Form.useForm();

  const surveyId = params?.surveyId;
  const [getSurveyById, { data, isLoading, refetch: refetchGetSurveyById }] = useLazyGetSurveyByIdQuery();

  const survey = data?.survey;

  const { data: customerConnectLogs, refetch: refetchCustomerConnectLogs } =
    useGetCustomerConnectLogsForSurveyQuery(surveyId);

  const { data: orderItemTypeData } = useGetOrderItemTypesQuery({
    all_pages: true
  });
  const orderItemTypes = orderItemTypeData?.order_item_types;
  const { data: orderItems, refetch: refetchOrderItems } = useGetOrderItemsQuery(surveyId);

  const [showModal, setShowModal] = useState(false);
  const [showFullView, setShowFullView] = useState(false);
  const [isOpen, setOpen] = useState(false);
  const [selectedCallLogData, setSelectedCallLogData] = useState({});
  const [isEditMode, setIsEditMode] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentType, setPaymentType] = useState("");
  const [selectedPayment, setSelectedPayment] = useState({});
  const [isUnpaid, setIsUnpaid] = useState(false);
  const [showInvoiceUploadModal, setShowInvoiceUploadModal] = useState(false);

  const [onCreateCustomerConnectLogForSurvey] = useCreateCustomerConnectLogForSurveyMutation();
  const [onUpdateCustomerConnectLogForSurvey] = useUpdateCustomerConnectLogForSurveyMutation();

  const [showOrderItemModal, setShowOrderItemModal] = useState(false);
  const [selectedOrderItemData, setSelectedOrderItemData] = useState({});

  const [onCreateOrderItemForSurvey] = useCreateOrderItemMutation();
  const [onUpdateOrderItemForSurvey] = useUpdateOrderItemMutation();
  const [onDeleteOrderItemForSurvey] = useDeleteOrderItemMutation();

  const [onSendQuotationPdf] = useSendQuotationPdfMutation();
  const [onViewQuotationReport] = useViewQuotationReportMutation();
  const [viewInvoiceReport] = useViewInvoiceReportMutation();
  const [onSendQuotation] = useSendQuotationMutation();
  const [onSendPaymentData] = useSendPaymentDataMutation();
  const [onSendInvoice] = useSendInvoiceMutation();

  const [cancel, { isLoading: cancelBookingInProgress, isError: cancelBookingError }] =
    useCancelSurveyMutation();

  const [onHold, { isLoading: onHoldBookingInProgress, isError: onHoldBookingError }] =
    useOnHoldSurveyMutation();

  const { data: payments, refetch: refetchPayments } = useGetPaymentsForSurveyQuery(surveyId);

  const { data: refunds, refetch: refetchRefunds } = useGetRefundsForSurveyQuery(surveyId);

  const [updatePayment] = useUpdatePaymentMutation();
  const [deletePayment] = useDeletePaymentMutation();
  const [deleteRefund] = useDeleteRefundMutation();
  const [updateRefund] = useUpdateRefundMutation();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalType, setModalType] = useState("");

  const user = auth.user;

  const isClient = user?.user_type === "client";

  const hasOrderItems = (orderItems?.order_items || []).length > 0;

  const isFinanceController = (user: any): boolean => {
    return isUserHasRoleTypes(user, ["finance_controller"]);
  };

  const isEditorUser = user?.roles?.some((role: { role_type: string }) =>
    ["finance_controller", "mis", "super_user"].includes(role.role_type)
  );

  const isManager = user?.roles?.some((role: { role_type: string }) =>
    ["city_manager", "zonal_manager"].includes(role.role_type)
  );

  const canEditOrderItem = isEditorUser || (isManager && !survey?.quotation_sent_to_api);

  const showFinanceTables = ["completed", "cancelled", "on_hold"].includes(survey?.status);

  const isInvoiceUploadingUser = user?.roles?.some(
    (role: { role_type: string }) =>
      ["mis", "super_user", "finance_controller"].includes(role.role_type) // management generally not considered editor user
  );

  useEffect(() => {
    console.log(4444, surveyId);
    if (surveyId) {
      getSurveyById(surveyId);
    }
  }, [surveyId]);


  const onGoBack = () => {
    navigate(-1);
  };

  const onUpdateCallLogModal = (callLog: any) => {
    setSelectedCallLogData(callLog);
    setIsEditMode(true);
    setShowModal(true);
  };

  const onUpdateOrderItemModal = (orderItem: any) => {
    setSelectedOrderItemData(orderItem);
    setIsEditMode(true);
    setShowOrderItemModal(true);
  };

  const onUpdatePayment = async (values: object, setLoading: any, form: any) => {
    try {
      setLoading(true);
      if (paymentType === "refund") {
        await updateRefund(values).unwrap();
      } else if (paymentType === "payment") {
        await updatePayment(values).unwrap();
      }
      api.success({
        message: `Update ${paymentType} to survey successful`,
        placement: "top"
      });
      refetchPaymentDetails();
      form.resetFields();
      setLoading(false);
      setShowPaymentModal(false);
    } catch (error: any) {
      api.info({
        message: `Add ${paymentType} to survey failed`,
        description: <ErrorDetails errors={error?.data || []} />,
        placement: "top"
      });
      setLoading(false);
    }
  };

  const onUpdatePaymentModal = (payment: any, payType: any) => {
    setShowPaymentModal(true);
    setSelectedPayment(payment);
    setIsEditMode(true);
    setPaymentType(payType);
  };

  const refetchPaymentDetails = () => {
    refetchPayments();
    refetchRefunds();
  };

  const onCancel = () => {
    setShowPaymentModal(false);
  };

  const onAddCallLog = async (values: object, setLoading: any, form: any) => {
    try {
      setLoading(true);
      await onCreateCustomerConnectLogForSurvey(values).unwrap();
      api.success({
        message: "Call log created for survey",
        placement: "top"
      });
      form.resetFields();
      setLoading(false);
      setShowModal(false);
      refetchCustomerConnectLogs();
    } catch (error: any) {
      api.info({
        message: "Create call log for survey operation failed",
        description: <ErrorDetails errors={error?.data || []} />,
        placement: "top"
      });
      setShowModal(false);
    }
  };

  const onUpdateCallLog = async (values: object, setLoading: any, form: any) => {
    try {
      setLoading(true);
      await onUpdateCustomerConnectLogForSurvey(values).unwrap();
      api.success({
        message: "Call log updated for survey",
        placement: "top"
      });
      form.resetFields();
      refetchCustomerConnectLogs();
      setLoading(false);
      setShowModal(false);
    } catch (error: any) {
      api.info({
        message: "Update call log for survey operation failed",
        description: <ErrorDetails errors={error?.data || []} />,
        placement: "top"
      });
      setShowModal(false);
    }
  };

  const onAddOrderItem = async (values: object, setLoading: any, form: any) => {
    try {
      setLoading(true);
      await onCreateOrderItemForSurvey(values).unwrap();
      api.success({
        message: "Order item created for survey",
        placement: "top"
      });
      form.resetFields();
      setLoading(false);
      setShowOrderItemModal(false);
      refetchOrderItems();
    } catch (error: any) {
      api.info({
        message: "Create order item for survey operation failed",
        description: <ErrorDetails errors={error?.data || []} />,
        placement: "top"
      });
      setShowOrderItemModal(false);
    }
  };

  const onDeleteOrderItem = async (assignmentId: any, orderItemId: any) => {
    const data = {
      assignmentId: assignmentId,
      orderItemId: orderItemId
    };
    try {
      await onDeleteOrderItemForSurvey(data).unwrap();
      api.success({
        message: "Order item deleted successfully",
        placement: "top"
      });
      form.resetFields();
      refetchOrderItems();
    } catch (error: any) {
      api.info({
        message: "Order item deletion operation failed",
        description: <ErrorDetails errors={error?.data || []} />,
        placement: "top"
      });
    }
  };

  const onUpdateOrderItem = async (values: object, setLoading: any, form: any) => {
    try {
      setLoading(true);
      await onUpdateOrderItemForSurvey(values).unwrap();
      api.success({
        message: "Call log updated for survey",
        placement: "top"
      });
      form.resetFields();
      refetchOrderItems();
      setLoading(false);
      setShowOrderItemModal(false);
    } catch (error: any) {
      api.info({
        message: "Update call log for survey operation failed",
        description: <ErrorDetails errors={error?.data || []} />,
        placement: "top"
      });
      setShowOrderItemModal(false);
    }
  };

  const handleCancelOrHold = (type: string) => {
    setModalType(type);
    setIsModalOpen(true);
  };

  const onFinish = async (values: any) => {
    if (modalType === "HOLD") {
      try {
        await onHold({ id: surveyId, reason: values.reason }).unwrap();
        api.success({
          message: "Survey marked as on hold operation successful",
          placement: "top"
        });
        form.resetFields();
        setOpen(false);
        refetchGetSurveyById();
        setIsModalOpen(false);
      } catch (error: any) {
        api.info({
          message: "Survey marked as on hold operation failed",
          description: <ErrorDetails errors={error?.data || []} />,
          placement: "top"
        });
      }
    }

    if (modalType === "CANCEL") {
      try {
        await cancel({ id: surveyId, reason: values.reason }).unwrap();
        api.success({
          message: "Survey cancelled successfully",
          placement: "top"
        });
        form.resetFields();
        setOpen(false);
        refetchGetSurveyById();
        setIsModalOpen(false);
      } catch (error: any) {
        api.info({
          message: "Survey cancelled operation failed",
          description: <ErrorDetails errors={error?.data || []} />,
          placement: "top"
        });
      }
    }
  };

  const handleCancel = () => {
    setModalType("");
    setIsModalOpen(false);
  };

  const onFinishFailed = (errorInfo: any) => {
    console.log("Failed:", errorInfo);
  };

  const onDeletePayment = async (assignmentId: any, paymentId: any) => {
    const data = {
      assignmentId: assignmentId,
      paymentId: paymentId
    };
    try {
      await deletePayment(data).unwrap();
      api.success({
        message: "Payment details deleted successfully",
        placement: "top"
      });
      form.resetFields();
      refetchCustomerConnectLogs();
      refetchPayments();
    } catch (error: any) {
      api.info({
        message: "Payment details deletion operation failed",
        description: <ErrorDetails errors={error?.data || []} />,
        placement: "top"
      });
    }
  };

  const onUploadInvoicePdf = async (values: any, setLoading: any, form: any, fileList: any) => {
    setLoading(true);

    try {
      const id = values["id"];
      const survey = new FormData();

      for (const key in values) {
        if (values?.[key]) {
          survey.append(`survey[${key}]`, values[key]);
        }
      }

      fileList.forEach((file: any) => {
        survey.append("survey[attachment]", file);
      });

      const response = await fetch(`${BASE_URL}print_reports/${id}/upload_invoice_pdf`, {
        method: "POST",
        headers: {
          "access-token": auth.accessToken,
          "token-type": "Bearer",
          client: auth.client,
          uid: auth.uid
        },
        body: survey
      });

      const responseData = await response.json();

      if (responseData?.pdf_url) {
        setLoading(false);
        api.success({
          message: "Invoice Pdf Upload Successful!",
          placement: "top"
        });
        setShowInvoiceUploadModal(false);
      } else {
        setLoading(false);
        setShowInvoiceUploadModal(false);
        api.error({
          message: "Invoice PDF upload failed",
          placement: "top"
        });
      }
    } catch (error: any) {
      setLoading(false);
      setShowInvoiceUploadModal(false);
      api.error({
        message: "Invoice PDF upload failed",
        placement: "top"
      });
    }
  };

  const addPaidOrderItemButton = (
    <Button
      icon={<DollarCircleOutlined />}
      onClick={() => {
        setShowOrderItemModal(true);
        setIsUnpaid(false);
      }}
    >
      Add Paid Order Item
    </Button>
  );

  const addUnpaidOrderItemButton = (
    <Button
      icon={<CreditCardOutlined />}
      onClick={() => {
        setShowOrderItemModal(true);
        setIsUnpaid(true);
      }}
    >
      Add Unpaid Order Item
    </Button>
  );

  const onDeleteRefund = async (assignmentId: any, refundId: any) => {
    const data = {
      assignmentId: assignmentId,
      refundId: refundId
    };
    try {
      await deleteRefund(data).unwrap();
      api.success({
        message: "Refund details deleted successfully",
        placement: "top"
      });
      form.resetFields();
      refetchCustomerConnectLogs();
      refetchRefunds();
    } catch (error: any) {
      api.info({
        message: "Refund details deletion operation failed",
        description: <ErrorDetails errors={error?.data || []} />,
        placement: "top"
      });
    }
  };

  const sendQuotationPdf = async () => {
    try {
      await onSendQuotationPdf(surveyId).unwrap();
      api.success({
        message: "Send Quotation Pdf Operation Completd Successfully",
        placement: "top"
      });
      form.resetFields();
      refetchCustomerConnectLogs();
      refetchRefunds();
    } catch (error: any) {
      api.info({
        message: "Send Quotation Pdf Operation failed",
        placement: "top"
      });
    }
  };

  const sendQuotation = async () => {
    try {
      const res = await onSendQuotation(surveyId).unwrap();
      if (res) {
        window.open(res, "_blank");
      }
      api.success({
        message: "Send Quotation Operation Completd Successfully",
        placement: "top"
      });
      form.resetFields();
      refetchCustomerConnectLogs();
      refetchRefunds();
    } catch (error: any) {
      api.info({
        message: "Send Quotation Operation failed",
        placement: "top"
      });
    }
  };

  const sendPaymentData = async () => {
    try {
      await onSendPaymentData(surveyId).unwrap();
      api.success({
        message: "Send Payment Data Operation Completd Successfully",
        placement: "top"
      });
      form.resetFields();
      refetchCustomerConnectLogs();
      refetchRefunds();
    } catch (error: any) {
      api.info({
        message: "Send Payment Data  Operation failed",
        placement: "top"
      });
    }
  };

  const viewQuotationReport = async () => {
    try {
      const res = await onViewQuotationReport(surveyId).unwrap();
      if (res) {
        window.open(res.pdf_url, "_blank");
      }
    } catch (error: any) {
      api.info({
        message: "View Quotation Operation failed",
        placement: "top"
      });
    }
  };

  const sendInvoice = async () => {
    try {
      await onSendInvoice(surveyId).unwrap();
      api.success({
        message: "Send Operation Completd Successfully",
        placement: "top"
      });
      form.resetFields();
      refetchCustomerConnectLogs();
      refetchRefunds();
    } catch (error: any) {
      api.info({
        message: "Send Invoice Operation failed",
        placement: "top"
      });
    }
  };

  const onViewInvoiceReport = async () => {
    try {
      const res = await viewInvoiceReport(surveyId).unwrap();
      if (res) {
        window.open(res.pdf_url, "_blank");
      }
    } catch (error: any) {
      api.info({
        message: "View Invoice Operation failed",
        placement: "top"
      });
    }
  };

  return (
    <>
      {contextHolder}
      {!isLoading && data && (
        <div style={{ paddingBottom: 50 }}>
          <Row style={{ marginBottom: 20 }}>
            <Col
              sm={24}
              style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
            >
              <h2>Survey Details</h2>
              <Button onClick={onGoBack} size="middle" type="default" icon={<LeftCircleOutlined />}>
                Back
              </Button>
            </Col>
          </Row>
          {!isClient && (
            <Row style={{ marginBottom: 20 }}>
              <Col
                sm={24}
                style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
              >
                <div></div>
                <div style={{ display: "flex", gap: 5, alignItems: "center" }}>
                  {data?.survey?.status && data?.survey?.status !== "completed" && (
                    <>
                      <Button icon={<StopOutlined />} onClick={() => handleCancelOrHold("CANCEL")}>
                        Cancel
                      </Button>
                      <Button
                        icon={<PauseCircleOutlined />}
                        onClick={() => handleCancelOrHold("HOLD")}
                      >
                        Hold
                      </Button>
                    </>
                  )}
                  {!isFinanceController(user) && (
                    <>
                      <Button icon={<PlusCircleOutlined />} onClick={() => setShowModal(true)}>
                        Create Call Connect Log
                      </Button>
                    </>
                  )}
                </div>
              </Col>
            </Row>
          )}

          <Descriptions
            title=""
            bordered
            column={1}
            labelStyle={{ fontWeight: 700, width: 250 }}
            size="middle"
          >
            <Descriptions.Item label="Survey Code">
              {data?.survey?.ecb_survey_code || "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Charger Type">{data?.survey?.obc || "-"}</Descriptions.Item>
          </Descriptions>
          <br />

          <h3>Customer Details</h3>

          <Descriptions
            title=""
            bordered
            column={1}
            labelStyle={{ fontWeight: 700, width: 250 }}
            size="middle"
          >
            <Descriptions.Item label="Customer">
              {data?.survey?.customer_name || "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Phone">
              {data?.survey?.contact_number || "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Booking Address">
              {data?.survey?.booking_address || "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Zone">{data?.survey?.zone?.name || "-"}</Descriptions.Item>
            <Descriptions.Item label="Address">{customerAddress(data?.survey)}</Descriptions.Item>
            {!isClient && (
              <Descriptions.Item label="EV City">
                {data?.survey?.city?.name || "-"}
              </Descriptions.Item>
            )}
            <Descriptions.Item label="Customer city of Survey/Installation">
              {data?.survey?.customer_city || "-"}
            </Descriptions.Item>
            {!isClient && (
              <Descriptions.Item label="Customer Rating">
                {data?.survey?.customer_rating ? `${data?.survey?.customer_rating}/5` : "-"}
              </Descriptions.Item>
            )}
          </Descriptions>

          <h3>Timelines</h3>

          <Descriptions
            title=""
            bordered
            column={1}
            labelStyle={{ fontWeight: 700, width: 250 }}
            size="middle"
          >
            <Descriptions.Item label="Status">
              {getStatusText(data?.survey)}
              {data?.survey?.status && data?.survey?.status === "cancelled" && (
                <>
                  <p style={{ margin: 0 }}>
                    Time: {localizeDateTime(data?.survey?.cancelled_at) || ""}
                  </p>
                  <p style={{ margin: 0 }}>Reason: {data?.survey?.cancel_reason || ""}</p>
                </>
              )}
              {data?.survey?.status && data?.survey?.status === "on_hold" && (
                <>
                  <p style={{ margin: 0 }}>Reason: {data?.survey?.on_hold_reason || ""}</p>
                </>
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Scheduled Date - Time">
              {data?.survey?.scheduled_survey_date_time
                ? localizeDateTime(data?.survey?.scheduled_survey_date_time)
                : "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Brand Booking Date">
              {data?.survey?.brand_booking_date
                ? localizeDate(data?.survey?.brand_booking_date)
                : "-"}
            </Descriptions.Item>
            <Descriptions.Item label="eCB Receive Date">
              {data?.survey?.ecb_receive_date ? localizeDate(data?.survey?.ecb_receive_date) : "-"}
            </Descriptions.Item>
            <Descriptions.Item label="eCB Booking Date">
              {data?.survey?.ecb_booking_date ? localizeDate(data?.survey?.ecb_booking_date) : "-"}
            </Descriptions.Item>
            <br />

            <Descriptions.Item label="First Contact">
              {data?.survey?.first_contact_at || "-"}
            </Descriptions.Item>

            <Descriptions.Item label="Created At">
              {data?.survey?.created_at ? localizeDateTime(data?.survey?.created_at) : "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Next Follow Up">
              {data?.survey?.next_follow_up_at
                ? localizeDateTime(data?.survey?.next_follow_up_at)
                : "-"}
            </Descriptions.Item>
            <br />
            <br />
            <Descriptions.Item label="Start Time">
              {data?.survey?.actual_survey_date_time
                ? localizeDateTime(data?.survey?.actual_survey_date_time)
                : "-"}
            </Descriptions.Item>
            <Descriptions.Item label="End Time">
              {data?.survey?.survey_completed_at
                ? localizeDateTime(data?.survey?.survey_completed_at)
                : "-"}
            </Descriptions.Item>
            <Descriptions.Item label="OTP Start Time">
              {data?.survey?.survey_start_otp_at
                ? localizeDateTime(data?.survey?.survey_start_otp_at)
                : "-"}
            </Descriptions.Item>
            <Descriptions.Item label="OTP End Time">
              {data?.survey?.survey_end_otp_at
                ? localizeDateTime(data?.survey?.survey_end_otp_at)
                : "-"}
            </Descriptions.Item>
          </Descriptions>

          <h3>Team</h3>

          <Descriptions
            title=""
            bordered
            column={1}
            labelStyle={{ fontWeight: 700, width: 250 }}
            size="middle"
          >
            {!isClient && (
              <>
                <Descriptions.Item label="City Manager">
                  {data?.survey?.assigned_city_manager?.name || "-"}
                </Descriptions.Item>
                <Descriptions.Item label="Fied Agent">
                  {data?.survey?.assigned_field_agent1?.name || "-"}
                </Descriptions.Item>
                <Descriptions.Item label="Partner">
                  {data?.survey?.survey_partner?.name || "-"}
                </Descriptions.Item>
              </>
            )}
            <Descriptions.Item label="Partner Mgr.">
              {data?.survey?.assigned_partner_user?.name || "-"}
            </Descriptions.Item>
          </Descriptions>

          <h3>Vehicle Details</h3>

          <Descriptions
            title=""
            bordered
            column={1}
            labelStyle={{ fontWeight: 700, width: 250 }}
            size="middle"
          >
            <Descriptions.Item label="Dealer">{data?.survey?.dealer_name || "-"}</Descriptions.Item>

            <Descriptions.Item label="Brand Model">
              {data?.survey?.brand_model?.name || "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Model Color">
              {data?.survey?.model_colour || "-"}
            </Descriptions.Item>
          </Descriptions>
          {isInvoiceUploadingUser && (
            <>
              <h3>Pricing</h3>
              <Descriptions
                title=""
                bordered
                column={1}
                labelStyle={{ fontWeight: 700, width: 250 }}
                size="middle"
              >
                <Descriptions.Item label="Complementary Cable Length">
                  {data?.survey?.complementary_cable_length || "-"}
                </Descriptions.Item>
                <Descriptions.Item label="Pricing Per Meter">
                  {data?.survey?.pricing_per_meter || "-"}
                </Descriptions.Item>
              </Descriptions>
              <br />
              <h3>Invoice Details</h3>
              <Descriptions
                title=""
                bordered
                column={1}
                labelStyle={{ fontWeight: 700, width: 250 }}
                size="middle"
              >
                <Descriptions.Item label="Invoice Number">
                  {data?.survey?.invoice_number || "-"}
                </Descriptions.Item>
                <Descriptions.Item label="Invoice Date">
                  {data?.survey?.invoice_date ? localizeDate(data?.survey?.invoice_date) : "-"}
                </Descriptions.Item>
                <Descriptions.Item label="Invoice Amount">
                  {data?.survey?.invoice_amount || "-"}
                </Descriptions.Item>
                <Descriptions.Item label="Invoice Remarks">
                  {data?.survey?.invoice_remarks || "-"}
                </Descriptions.Item>
              </Descriptions>

              <br />
              <h3>Amount Details</h3>
              <Descriptions
                title=""
                bordered
                column={1}
                labelStyle={{ fontWeight: 700, width: 250 }}
                size="middle"
              >
                <Descriptions.Item label="Amount To Receive">
                  {data?.survey?.amount_to_receive || "-"}
                </Descriptions.Item>
                <Descriptions.Item label="Amount To Refund">
                  {data?.survey?.amount_to_refund || "-"}
                </Descriptions.Item>

                <Descriptions.Item label="Planned Start Date">
                  {data?.survey?.planned_start_date || "-"}
                </Descriptions.Item>
                <Descriptions.Item label="Complaint Code">
                  {data?.survey?.complaint_code || "-"}
                </Descriptions.Item>
                <Descriptions.Item label="Complaint Given By">
                  {data?.survey?.complaint_given_by || "-"}
                </Descriptions.Item>
                <Descriptions.Item label="Product Type">
                  {data?.survey?.product_type || "-"}
                </Descriptions.Item>
                <Descriptions.Item label="SR Type">
                  {data?.survey?.sr_type || "-"}
                </Descriptions.Item>
                <Descriptions.Item label="Email 2">{data?.survey?.email2 || "-"}</Descriptions.Item>
                <Descriptions.Item label="Source">{data?.survey?.source || "-"}</Descriptions.Item>
                <Descriptions.Item label="Created By">
                  {data?.survey?.created_by || "-"}
                </Descriptions.Item>
                <Descriptions.Item label="Brand Initiated Date">
                  {data?.survey?.brand_initiated_date || "-"}
                </Descriptions.Item>
                <Descriptions.Item label="Brand Unique ID">
                  {data?.survey?.brand_unique_id || "-"}
                </Descriptions.Item>
                <Descriptions.Item label="Dealer Code">
                  {data?.survey?.dealer_code || "-"}
                </Descriptions.Item>
                <Descriptions.Item label="Dealer Branch Code">
                  {data?.survey?.dealer_branch_code || "-"}
                </Descriptions.Item>
                <Descriptions.Item label="Brand Reshedule ID">
                  {data?.survey?.brand_reschedule_id || "-"}
                </Descriptions.Item>
                <Descriptions.Item label="Appointment Taken On">
                  {data?.survey?.appointment_taken_on
                    ? localizeDateTime(data?.survey?.appointment_taken_on)
                    : "-"}
                </Descriptions.Item>
                <Descriptions.Item label="Brand Modified By">
                  {data?.survey?.brand_modified_by || "-"}
                </Descriptions.Item>
                <Descriptions.Item label="Brand Modified Date">
                  {data?.survey?.brand_modified_date
                    ? localizeDateTime(data?.survey?.brand_modified_date)
                    : "-"}
                </Descriptions.Item>
                <Descriptions.Item label="Brand City Code">
                  {data?.survey?.brand_city_code || "-"}
                </Descriptions.Item>
                <Descriptions.Item label="Brand Booking Code">
                  {data?.survey?.brand_booking_code || "-"}
                </Descriptions.Item>
              </Descriptions>
            </>
          )}

          {!showFullView && (
            <Button type="link" onClick={() => setShowFullView(true)} style={{ marginTop: "20px" }}>
              Show More
            </Button>
          )}

          {showFullView && (
            <>
              <br />
              <Descriptions
                title=""
                bordered
                column={1}
                labelStyle={{ fontWeight: 700, width: 250 }}
                size="middle"
              >
                <Descriptions.Item label="Brand Booking Code">
                  {data?.survey?.brand_booking_code || "-"}
                </Descriptions.Item>
                <Descriptions.Item label="Dealer Location">
                  {data?.survey?.dealer_location || "-"}
                </Descriptions.Item>
                <Descriptions.Item label="Booking Address">
                  {data?.survey?.booking_address || "-"}
                </Descriptions.Item>
                <Descriptions.Item label="Cust. Alt. Contact">
                  {data?.survey?.alt_contact_number || "-"}
                </Descriptions.Item>
                <Descriptions.Item label="Record - Cust. Ok?">
                  {data?.survey?.customer_ok_with_recording ? "YES" : "NO"}
                </Descriptions.Item>

                <br />

                <Descriptions.Item label="Model Description">
                  {data?.survey?.model_description || "-"}
                </Descriptions.Item>
                <Descriptions.Item label="Variant">
                  {data?.survey?.model_variant || "-"}
                </Descriptions.Item>
                <Descriptions.Item label="Int Color">
                  {data?.survey?.model_int_color || "-"}
                </Descriptions.Item>
                <Descriptions.Item label="Ext Color">
                  {data?.survey?.model_ext_color || "-"}
                </Descriptions.Item>

                <br />

                <Descriptions.Item label="Latitude">
                  {data?.survey?.latitude || "-"}
                </Descriptions.Item>
                <Descriptions.Item label="Longitude">
                  {data?.survey?.longitude || "-"}
                </Descriptions.Item>
                <Descriptions.Item label="Outstation for installer">
                  {data?.survey?.is_outstation ? "YES" : "NO"}
                </Descriptions.Item>
                <Descriptions.Item label="Outstation for client">
                  {data?.survey?.is_outstation_for_client ? "YES" : "NO"}
                </Descriptions.Item>

                <Descriptions.Item label="Site POC Name">
                  {data?.survey?.site_poc_name || "-"}
                </Descriptions.Item>
                <Descriptions.Item label="Site POC Contact">
                  {data?.survey?.site_poc_contact || "-"}
                </Descriptions.Item>

                <br />

                <Descriptions.Item label="Cable Length">
                  {data?.survey?.cable_length_new || data?.survey?.cable_length || "-"}
                </Descriptions.Item>
                <Descriptions.Item label="Cable Gauge">
                  {data?.survey?.cable_gauge || "-"}
                </Descriptions.Item>
                <Descriptions.Item label="Charger Capacity">
                  {data?.survey?.charger_capacity || "-"}
                </Descriptions.Item>
                <Descriptions.Item label="Charger Make">
                  {data?.survey?.charger_make_type || "-"}
                </Descriptions.Item>

                <br />

                <Descriptions.Item label="Extra Cable">
                  {data?.survey?.extra_cable_customer_pay ? "Yes" : "No"}
                </Descriptions.Item>

                <Descriptions.Item label="MDI">{data?.survey?.mdi || "-"}</Descriptions.Item>
                <Descriptions.Item label="VDC">{data?.survey?.vdc || "-"}</Descriptions.Item>
                <Descriptions.Item label="Sanctioned Load">
                  {data?.survey?.sanctioned_load || "-"}
                </Descriptions.Item>
                <Descriptions.Item label="Average Load Utilised">
                  {data?.survey?.avg_load_utilized || "-"}
                </Descriptions.Item>
                <Descriptions.Item label="Note for Agent">
                  {data?.survey?.agent_notes || "-"}
                </Descriptions.Item>
                <Descriptions.Item label="Broad Survey Findings 1">
                  {data?.survey?.summary_stage1 || "-"}
                </Descriptions.Item>
                <Descriptions.Item label="Broad Survey Findings 2">
                  {data?.survey?.summary_stage2 || "-"}
                </Descriptions.Item>
                <Descriptions.Item label="Remarks">
                  {data?.survey?.remarks || "-"}
                </Descriptions.Item>

                <br />

                <Descriptions.Item label="Company Name">
                  {data?.survey?.company_name || "-"}
                </Descriptions.Item>
                <Descriptions.Item label="Customer GST">
                  {data?.survey?.gst || "-"}
                </Descriptions.Item>

                <Descriptions.Item label="Flag">{data?.survey?.flag || "-"}</Descriptions.Item>
                <Descriptions.Item label="Is this client (OEM) paid - Survey ?">
                  {data?.survey?.customer_billing ? "Client/OEM paid" : "Customer/Dealer paid"}
                </Descriptions.Item>
              </Descriptions>
            </>
          )}
          {showFullView && (
            <Button
              type="link"
              onClick={() => setShowFullView(false)}
              style={{ marginTop: "20px" }}
            >
              Show Less
            </Button>
          )}
          {!isFinanceController(user) && (
            <>
              <ConnectLogTabel
                title="Survey customer connect logs"
                data={customerConnectLogs?.customer_connect_logs || []}
                msil={data?.survey?.brand?.send_reports_to_api}
                onUpdateCallLogModal={onUpdateCallLogModal}
              />
            </>
          )}

          {showFinanceTables && data?.survey?.brand?.send_reports_to_api && (
            <OrderItemTable
              title="Survey order items"
              addPaidOrderItemButton={addPaidOrderItemButton}
              addUnpaidOrderItemButton={addUnpaidOrderItemButton}
              data={orderItems?.order_items || []}
              onUpdateOrderItemModal={onUpdateOrderItemModal}
              onDeleteOrderItem={onDeleteOrderItem}
              assignmentId={surveyId}
              canEditOrderItem={canEditOrderItem}
            />
          )}
          {hasOrderItems && (
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: "8px",
                marginTop: "16px"
              }}
            >
              {canEditOrderItem && (
                <>
                  <Button
                    size="middle"
                    type="primary"
                    icon={<EyeOutlined />}
                    onClick={() => viewQuotationReport()}
                  >
                    View Quotation
                  </Button>
                </>
              )}
              {isEditorUser && (
                <>
                  <Button
                    size="middle"
                    type="primary"
                    icon={<FilePdfOutlined />}
                    onClick={sendQuotationPdf}
                  >
                    Send Quotation PDF
                  </Button>
                  <Button
                    size="middle"
                    type="primary"
                    icon={<MailOutlined />}
                    onClick={sendQuotation}
                  >
                    Send Quotation
                  </Button>
                  <Button
                    size="middle"
                    type="primary"
                    icon={<DollarCircleOutlined />}
                    onClick={sendPaymentData}
                  >
                    Send Payment Data
                  </Button>
                  {/* <Button
                    size="middle"
                    type="primary"
                    icon={<FilePdfOutlined />}
                    onClick={sendInvoice}
                  >
                    Send Invoice PDF
                  </Button> */}
                  {/* <Button
                    size="middle"
                    type="primary"
                    icon={<SnippetsOutlined />}
                    onClick={onViewInvoiceReport}
                  >
                    View Invoice Report
                  </Button> */}
                  <Button
                    size="middle"
                    type="primary"
                    icon={<UploadOutlined />}
                    title="Upload Invoice PDF"
                    onClick={() => setShowInvoiceUploadModal(true)}
                  >
                    Upload Invoice PDF
                  </Button>
                </>
              )}
            </div>
          )}
        </div>
      )}
      {isLoading && (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100vh"
          }}
        >
          <LoadingOutlined style={{ fontSize: 24 }} spin />
        </div>
      )}

      {showModal && (
        <ConnectLogForm
          assignment={survey}
          title={isEditMode ? "Update customer connect logs" : "Create customer connect logs"}
          isOpen={showModal}
          msil={data?.survey?.brand?.send_reports_to_api}
          nextFollowUpRequired={data?.survey?.brand?.next_follow_up_required}
          handleCancel={() => {
            setShowModal(false);
            setIsEditMode(false);
          }}
          isEditMode={isEditMode}
          onUpdateCallLog={isEditMode && onUpdateCallLog}
          selectedCallLogData={isEditMode ? selectedCallLogData : {}}
          onAddCallLog={!isEditMode && onAddCallLog}
          booking
        />
      )}

      {showOrderItemModal && (
        <OrderItemForm
          assignment={survey}
          title={
            isEditMode
              ? "Update Order Item"
              : isUnpaid
                ? "Create Unpaid Order Item"
                : "Create Paid Order Item"
          }
          isOpen={showOrderItemModal}
          handleCancel={() => {
            setShowOrderItemModal(false);
            setIsEditMode(false);
          }}
          isEditMode={isEditMode}
          enableAdditionalCharges={isInvoiceUploadingUser}
          onUpdateOrderItem={isEditMode && onUpdateOrderItem}
          selectedOrderItemData={isEditMode ? selectedOrderItemData : {}}
          onAddOrderItem={!isEditMode && onAddOrderItem}
          orderItemTypes={orderItemTypes}
          isUnpaid={isUnpaid}
        />
      )}

      <AddPaymentModal
        isModalOpen={showPaymentModal}
        headerText={`Survey: ${survey?.ecb_survey_code}`}
        isRefund={paymentType === "refund"}
        selectedPayment={selectedPayment}
        assignment={survey}
        title={isEditMode ? "Update" : "Add"}
        onUpdatePayment={onUpdatePayment}
        isEditMode={isEditMode}
        onCancel={onCancel}
      />

      {showFinanceTables && isInvoiceUploadingUser && (
        <>
          <PaymentTable
            title="Payments Details"
            data={payments?.payments || []}
            onUpdatePaymentModal={onUpdatePaymentModal}
            onDeletePayment={onDeletePayment}
            assignmentId={surveyId}
          />
          <RefundTable
            title="Refund Details"
            data={refunds?.refunds || []}
            onUpdatePaymentModal={onUpdatePaymentModal}
            onDeleteRefund={onDeleteRefund}
            assignmentId={surveyId}
          />
        </>
      )}

      {showInvoiceUploadModal && (
        <UploadInvoicePdfModal
          isModalOpen={showInvoiceUploadModal}
          assignment={survey}
          onCancel={() => setShowInvoiceUploadModal(false)}
          onUploadInvoicePdf={onUploadInvoicePdf}
        />
      )}

      <>
        <Modal
          title={`${modalType === "HOLD" ? "Put Survey on Hold" : "Cancel Survey"}`}
          open={isModalOpen}
          onCancel={handleCancel}
          footer={[
            <Button key="cancel" onClick={handleCancel}>
              Cancel
            </Button>,
            <Button form="myForm" key="submit" htmlType="submit" type="primary">
              Submit
            </Button>
          ]}
        >
          <Form
            name="myForm"
            form={form}
            labelCol={{ span: 24 }}
            wrapperCol={{ span: 24 }}
            style={{ maxWidth: 600 }}
            initialValues={{ remember: true }}
            onFinish={onFinish}
            onFinishFailed={onFinishFailed}
            autoComplete="off"
          >
            <Form.Item
              label={modalType === "HOLD" ? "Reason for puting on hold" : "Reason for cancel"}
              name="reason"
              rules={[{ required: true, message: "Please input reason!" }]}
            >
              <Select
                placeholder="Select status"
                optionFilterProp="children"
                showSearch
                filterOption={(inputValue, option: any) =>
                  option.label.toLowerCase().includes(inputValue.toLowerCase())
                }
                options={modalType === "HOLD" ? holdOptions : cancelOptions}
              ></Select>
            </Form.Item>
          </Form>
        </Modal>
      </>
    </>
  );
};

export default ViewSurvey;
